import React from 'react'
import * as Yup from 'yup'

import { TtAvatar, TtButton, TtCard } from '@23/ui-react'
import * as TtForms from '@23/react-forms'

import { useSite } from '../../../../../../../sharedLegacy/context/SiteContext'
import { getAbsoluteUrl } from '../../../../../../../sharedLegacy/utils'
import { useVideoPreviewSettingsContext } from '../../../../VideoPreviewSettingsContext'
import type { WidgetDTO } from '../../../../../../model/Widget'

const businessCardSchema = Yup.object({
  name: Yup.string().required('Full name is required').trim(),
  title: Yup.string().trim(),
  phone: Yup.string().trim(),
  email: Yup.string()
    .trim()
    .transform((v) => (v === '' ? undefined : v))
    .optional()
    .email('Invalid email format'),
})

type FormValues = Yup.InferType<typeof businessCardSchema>

interface WidgetBusinessCardEditModeProps {
  widget: WidgetDTO
  onSave: (values: FormValues) => Promise<void>
  onCancel: () => void
}

const BusinessCardEditMode = ({
  widget,
  onSave,
  onCancel,
}: WidgetBusinessCardEditModeProps) => {
  const { site_url: siteUrl } = useSite()
  const avatarUrl = getAbsoluteUrl(widget.avatar, siteUrl)
  const { logoSrc } = useVideoPreviewSettingsContext()

  return (
    <TtCard
      className="personal-widget-business-card personal-widget-business-card--edit"
      hideLabel
    >
      <div className="personal-widget-business-card-form">
        <div className="personal-widget-business-card-form-header">
          <div className="personal-widget-business-card-logo-left">
            {avatarUrl && (
              <TtAvatar
                label={widget.name}
                image={avatarUrl}
                size="xl"
                className="personal-widget-business-card-avatar"
              />
            )}
          </div>
          {logoSrc && (
            <div className="personal-widget-business-card-brand-right">
              <img
                src={logoSrc}
                alt="Site logo"
                className="personal-widget-business-card-logo"
              />
            </div>
          )}
        </div>
        <TtForms.Form<FormValues>
          className="personal-widget-business-card-form-fields"
          label="Edit business card"
          initialValues={{
            name: widget.name ?? '',
            title: widget.title ?? '',
            phone: widget.phone ?? '',
            email: widget.email ?? '',
          }}
          validationSchema={businessCardSchema}
          onSubmit={onSave}
          enableReinitialize
        >
          <TtForms.TextField
            name="name"
            label="Full Name"
            placeholder="Full Name"
            required
          />
          <TtForms.TextField name="title" label="Role" placeholder="Role" />
          <TtForms.TextField
            name="phone"
            label="Phone"
            placeholder="Phone Number"
          />
          <TtForms.TextField name="email" label="Email" placeholder="Email" />
          <div className="personal-widget-business-card-form-actions">
            <TtButton variant="outline" color="secondary" onClick={onCancel}>
              Cancel
            </TtButton>
            <TtForms.SubmitButton color="primary" disabled={false}>
              Save
            </TtForms.SubmitButton>
          </div>
        </TtForms.Form>
      </div>
    </TtCard>
  )
}

export { type FormValues as BusinessCardFormValues, BusinessCardEditMode }
